import React, { useRef, useState } from 'react';

import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Typography, Link } from '@mui/material';

import {
  ErrorsContainer,
  FilePreviewContainer,
  FileUploadContainer,
  FormField,
  InputLabel,
  MoreInformation,
  SpinnerContainer,
} from './FileUploadV2.styles';

import successIcon from '../../../assets/images/successIcon.svg';
import { API_URLS, APPLICATION_HEADERS } from '../../../components/common/headers';
import { Block } from '../../../components/common/StyledComponents/StyledBlock';
import MaterialErrorIcon from '../../../components/core/MaterialIcons/MaterialErrorIcon';
import LoadingSpinner from '../../../components/MySurveys/LoadingSpinner';
import { protectApiViaOkta } from '../../../utils/protectApiViaOkta';

const DEFAULT_MAX_FILE_SIZE_IN_BYTES = 500000;

export const FileUploadV2 = ({
  label,
  updateFilesCb,
  maxFileSizeInBytes = DEFAULT_MAX_FILE_SIZE_IN_BYTES,
  ...otherProps
}) => {
  const fileInputField = useRef(null);
  const { providerEmail, dealID } = otherProps;
  const [showProgress, setShowProgress] = React.useState(false);
  const [errors, setErrors] = React.useState();
  const [errorCount, setErrorCount] = React.useState();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = React.useState();

  const handleNewFileUpload = (e) => {
    const { files: newFiles } = e.target;

    if (newFiles.length) {
      handleFileUpload(e);
    }
  };

  const handleFileUpload = async (event) => {
    event.preventDefault();
    const currentFile = event.target.files[0];

    const filename = currentFile.name;

    setUploadedFileName(filename);
    const extension = filename.split('.').pop();

    if (!providerEmail || providerEmail === undefined) {
      setErrors('Please enter your email before uploading the file');
      setShowProgress(true);
      setErrorCount(1);

      return;
    }

    if (extension !== 'xlsx') {
      setErrors('Please upload .xlsx file only');
      setShowProgress(true);
      setErrorCount(1);

      return;
    }

    setIsLoading(true);

    const URL = API_URLS.fileUploadURL;
    const formData = new FormData();

    formData.append('file', currentFile);
    formData.append('email', providerEmail);

    protectApiViaOkta('post', URL, formData)
      .then((res) => {
        setIsLoading(false);

        if (res && res.status === 202) {
          setErrors('');
          setShowProgress(true);
          if (dealID) {
            saveToAirtable(dealID);
          }
        }
      })
      .catch((error) => {
        setIsLoading(false);
        if (error.response && error.response.data) {
          const response = error.response.data;

          if (typeof response === 'string') {
            const errorsArray = response.split(',');

            errorsArray.length > 1
              ? setErrorCount(errorsArray.length - 1)
              : setErrorCount(errorsArray.length);
            setErrors(response);
            setShowProgress(true);
          }

          if (typeof response === 'object') {
            setErrorCount(1);
            setErrors(response.message);
            setShowProgress(true);
          }
        }
      });
  };

  const saveToAirtable = (id) => {
    const URL = API_URLS.updateDealURL;

    const updateObj = {
      dealId: id,
      locationUploadStatus: 'Yes',
    };

    protectApiViaOkta('put', URL, updateObj)
      .then((res) => {
        if (res && res.status === 200) {
          console.log('Deal update success');
        }
      })
      .catch((error) => {
        console.error('Error during update deal information', error);
      });
  };

  return (
    <>
      <FileUploadContainer>
        <InputLabel>{label}</InputLabel>
        <UploadFileIcon />
        <Typography variant="body1" mt={1}>
          <Link component="button">Click to upload</Link> or drag & drop your file to this area
        </Typography>
        <Typography variant="body2" color="text.secondary">
          For more information about how we use the data you provide, please refer to our
        </Typography>
        <FormField
          type="file"
          ref={fileInputField}
          onChange={handleNewFileUpload}
          title=""
          value=""
          {...otherProps}
        />
        <Typography variant="body2" color="text.secondary">
          <Link
            href={API_URLS.privacyPolicyURL}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{ display: 'inline-block', position: 'relative', zIndex: 1 }}
          >
            Global Web Privacy and Cookie Policy
          </Link>
        </Typography>
      </FileUploadContainer>
      <FilePreviewContainer>
        <MoreInformation>
          For more information about how we use the data you provide please refer to our
          <a href={API_URLS.privacyPolicyURL} target="_blank" rel="noreferrer">
            Global Web Privacy and Cookie Policy
          </a>
        </MoreInformation>
        {showProgress && (
          <Block>
            <Block display="flex">
              {!errors && (
                <Block display="flex" gap="8">
                  <Block className="magenta">
                    <img src={successIcon} width="24"></img>
                  </Block>
                  <Block className="green">
                    <Block className="successMsg">
                      "{uploadedFileName}" upload is in progress. You will receive an email once the
                      process is completed. You can upload another file by clicking on Upload File
                      button
                    </Block>
                  </Block>
                </Block>
              )}
            </Block>
            {errors && (
              <Block display="flex" gap="8">
                <Block>
                  <MaterialErrorIcon sx={{ fontSize: '24px' }}></MaterialErrorIcon>
                </Block>
                <Block className="green">
                  <Block className="errMsg">Upload failed : {errorCount} Error(s) found</Block>
                  <ErrorsContainer>
                    {errors &&
                      errors.split(',').map((text) => {
                        if (text) {
                          return (
                            <span key={text}>
                              {text}
                              <br />
                            </span>
                          );
                        }
                      })}
                  </ErrorsContainer>
                </Block>
              </Block>
            )}
          </Block>
        )}

        {isLoading && (
          <SpinnerContainer>
            <LoadingSpinner />
          </SpinnerContainer>
        )}
      </FilePreviewContainer>
    </>
  );
};
